import { observeStore } from '@ff-os/pdp-shared-redux/observeStore';
import { PdpStore } from '@ff-os/pdp-shared-redux/store';
import { dispatchImages } from './dispatchImages';
import { fetchAllAsFiles, FetchError } from './fetchAllAsFiles';
import { postToHost } from './postToHost';

export type AddUserImagesResult = {
    succeeded: number;
    failed: number;
    errors: FetchError[];
};

type PendingCall = {
    urls: string[];
    resolve: (result: AddUserImagesResult) => void;
};

declare global {
    interface Window {
        __editor_addUserImagesFromUrls?: (urls: string[]) => Promise<AddUserImagesResult>;
    }
}

export const registerAddUserImagesFromUrls = (store: PdpStore) => {

    const pendingCalls: PendingCall[] = [];
    let ready = false;

    const run = async (urls: string[]): Promise<AddUserImagesResult> => {
        const total = urls.length;
        postToHost({ type: 'addUserImages:start', total });

        const { files, errors } = await fetchAllAsFiles(urls);
        const dispatchErrors = dispatchImages(store, files);

        const allErrors = errors.concat(dispatchErrors);
        const succeeded = dispatchErrors.length === 0 ? files.length : 0;
        const failed = total - succeeded;

        const result: AddUserImagesResult = { succeeded, failed, errors: allErrors };
        postToHost({ type: 'addUserImages:done', ...result });
        return result;
    };

    const flushQueue = () => {
        while (pendingCalls.length > 0) {
            const call = pendingCalls.shift()!;
            run(call.urls).then(call.resolve);
        }
    };

    window.__editor_addUserImagesFromUrls = (urls: string[]) => {
        if (ready) {
            return run(urls);
        }
        return new Promise<AddUserImagesResult>(resolve => {
            pendingCalls.push({ urls, resolve });
        });
    };

    const selectLoadStatus = (state: any) => state.projectLoading?.loadStatus;

    const handleLoadStatus = (loadStatus: string | undefined) => {
        if (ready || loadStatus !== 'ready') {
            return;
        }
        ready = true;
        postToHost({ type: 'editor:ready' });
        flushQueue();
    };

    handleLoadStatus(selectLoadStatus(store.getState()));
    observeStore(store, selectLoadStatus, handleLoadStatus);
};